import { useState, useEffect } from 'react';
import { loadFromSessionStorage, saveToSessionStorage } from './tableSession.js';

/**
 * Hook for managing table state with session storage persistence
 * @param {string} id - Unique identifier for the table
 * @param {Object} [options]
 * @param {{ columnKey: string|null, direction: 'asc'|'desc'|null }} [options.defaultSortState] - Used when nothing is stored in session (first visit)
 */
export const useTableState = (id, { defaultSortState } = {}) => {
  const sortFallback = defaultSortState ?? {
    columnKey: null,
    direction: null
  };
  const [sortState, setSortState] = useState(() =>
    loadFromSessionStorage(id, 'sortState', sortFallback)
  );

  const [filterValues, setFilterValues] = useState(() =>
    loadFromSessionStorage(id, 'filterValues', {})
  );

  const [disabledFilters, setDisabledFilters] = useState(() =>
    loadFromSessionStorage(id, 'disabledFilters', new Set())
  );

  const [paginationState, setPaginationState] = useState(() =>
    loadFromSessionStorage(id, 'paginationState', {
      currentPage: 1,
      pageSize: 20
    })
  );

  const [highlightedRows, setHighlightedRows] = useState(() =>
    loadFromSessionStorage(id, 'highlightedRows', new Set())
  );

  useEffect(() => {
    saveToSessionStorage(id, 'sortState', sortState);
  }, [id, sortState]);

  useEffect(() => {
    saveToSessionStorage(id, 'filterValues', filterValues);
  }, [id, filterValues]);

  useEffect(() => {
    saveToSessionStorage(id, 'disabledFilters', disabledFilters);
  }, [id, disabledFilters]);

  useEffect(() => {
    saveToSessionStorage(id, 'paginationState', paginationState);
  }, [id, paginationState]);

  useEffect(() => {
    saveToSessionStorage(id, 'highlightedRows', highlightedRows);
  }, [id, highlightedRows]);

  return {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
    highlightedRows,
    setHighlightedRows
  };
};
